'use client';

import { Button, ChevronIcon, CopyButton, DropdownButton, SwitchIcon, AccountIcon } from '@/components';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useToggle } from 'react-use';
import { useOnClickOutside } from 'usehooks-ts';
import { formatWalletAddress } from '@/utils/format';

export const WalletDropdown = ({
  accountAddress,
  onDisconnectWallet,
  iconSrc,
  name,
}: {
  accountAddress: string;
  onDisconnectWallet: () => void;
  iconSrc?: string;
  name?: string;
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const [isOpen, toggleIsOpen] = useToggle(false);
  const accountDisplayName = formatWalletAddress(accountAddress, 3, 3);

  useEffect(() => {
    // close dropdown when account address or pathname changes
    if (isOpen) {
      toggleIsOpen(false);
    }
    // don't put isOpen as a dependency here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, toggleIsOpen]);

  function handleClickOutside(event: MouseEvent | TouchEvent | FocusEvent) {
    if (dropdownRef.current) {
      if (
        !dropdownRef.current.contains(event.target as Node) &&
        !dropdownRef?.current?.parentElement?.contains(event.target as Node)
      ) {
        toggleIsOpen(false);
      }
    }
  }

  useOnClickOutside(dropdownRef, handleClickOutside);

  return (
    <div className="relative" data-step="wallet">
      <DropdownButton className="relative" onClick={toggleIsOpen}>
        <div className="flex items-center gap-x-2 " ref={dropdownButtonRef}>
          <AccountIcon walletIconSrc={iconSrc} size={24} />
          <p className="hidden md:flex items-center text-xs">
            {accountDisplayName}
            <ChevronIcon />
          </p>
        </div>
      </DropdownButton>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="flex flex-col gap-y-4 absolute right-0 -bottom-5 w-[240px] translate-y-[100%] rounded-xl border border-neutral-900 p-4 bg-neutral-950 shadow-xl text-neutral-600"
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-x-2">
              <AccountIcon walletIconSrc={iconSrc} size={32} />
              <div>
                <p className="flex items-center gap-x-1 text-sm">
                  {accountDisplayName}
                  <CopyButton size="sm" copyValue={accountAddress} />
                </p>
                {name && <p className="text-xs">{name}</p>}
              </div>
            </div>
            <Button onClick={onDisconnectWallet} className="rounded-lg px-2" variant="secondary">
              <SwitchIcon />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
